// @ts-strict-ignore
import type {
  ArrayConfigFieldData,
  ArrayConfigFieldSchema,
  ConfigFieldData,
  ConfigFieldSchema,
  NumberConfigFieldSchema,
  ObjectConfigFieldData,
  ObjectConfigFieldSchema,
  StringConfigFieldSchema,
} from '@empathyco/playboard-core-domain'
import type {
  ArrayServiceConfigFieldValidityState,
  BasicServiceConfigFieldValidityState,
  Dictionary,
  ObjectServiceConfigFieldValidityState,
  ServiceConfigFieldError,
  ServiceConfigFieldValidityState,
} from '../models'
import { ConfigFieldTypeEnum, ConfigFieldVisibilityEnum } from '@empathyco/playboard-core-domain'
import { ServiceConfigFieldErrorEnum } from '../enums'

export class InstanceManagementUtils {
  static getServiceConfigFieldFormData(
    schema: ConfigFieldSchema,
    data?: ConfigFieldData,
  ): ConfigFieldData {
    const typeFn: () => ObjectConfigFieldData = {
      [ConfigFieldTypeEnum.OBJECT]: () =>
        this.getObjectServiceConfigFieldFormData(
          schema as ObjectConfigFieldSchema,
          data as ObjectConfigFieldData,
        ),
      [ConfigFieldTypeEnum.ARRAY]: () =>
        this.getArrayServiceConfigFieldFormData(
          schema as ArrayConfigFieldSchema,
          data as ArrayConfigFieldData,
        ),
    }[schema.type]

    return typeFn ? typeFn() : (data ?? schema.default)
  }

  static getServiceConfigFieldValidityState(
    schema: ConfigFieldSchema,
    data: ConfigFieldData,
    required = false,
  ): ServiceConfigFieldValidityState {
    if (data === null || data === undefined || data === '') {
      return {
        errors: required ? [{ id: ServiceConfigFieldErrorEnum.REQUIRED }] : [],
        ...(schema.type === ConfigFieldTypeEnum.OBJECT && { properties: {} }),
        ...(schema.type === ConfigFieldTypeEnum.ARRAY && { items: {} }),
      }
    }
    const typeFn: () => ServiceConfigFieldValidityState = {
      [ConfigFieldTypeEnum.OBJECT]: () =>
        this.getObjectServiceConfigFieldValidityState(
          schema as ObjectConfigFieldSchema,
          data as ObjectConfigFieldData,
        ),
      [ConfigFieldTypeEnum.ARRAY]: () =>
        this.getArrayServiceConfigFieldValidityState(
          schema as ArrayConfigFieldSchema,
          data as ArrayConfigFieldData,
        ),
      [ConfigFieldTypeEnum.STRING]: () =>
        this.getStringServiceConfigFieldValidityState(
          schema as StringConfigFieldSchema,
          data as string,
        ),
      [ConfigFieldTypeEnum.NUMBER]: () =>
        this.getNumberServiceConfigFieldValidityState(
          schema as NumberConfigFieldSchema,
          data as number,
        ),
      [ConfigFieldTypeEnum.INTEGER]: () =>
        this.getNumberServiceConfigFieldValidityState(
          schema as NumberConfigFieldSchema,
          data as number,
        ),
    }[schema.type]
    return typeFn ? typeFn() : { errors: [] }
  }

  static getObjectServiceConfigFieldVisiblePropertyIds(schema: ObjectConfigFieldSchema): string[] {
    return Object.entries(schema.properties)
      .reduce((ids: string[], [id, { front_metadata }]: [string, ConfigFieldSchema]) => {
        if (front_metadata?.visibility !== ConfigFieldVisibilityEnum.HIDDEN) {
          ids.push(id)
        }
        return ids
      }, [])
      .sort((id1: string, id2: string) => {
        const indexOfId1 = schema.front_metadata?.properties_order?.indexOf(id1)
        const indexOfId2 = schema.front_metadata?.properties_order?.indexOf(id2)
        if (indexOfId2 < 0) {
          return -1
        }
        if (indexOfId1 < 0) {
          return 1
        }
        return indexOfId1 - indexOfId2
      })
  }

  private static getObjectServiceConfigFieldFormData(
    schema: ObjectConfigFieldSchema,
    data: ObjectConfigFieldData,
  ): ObjectConfigFieldData {
    return Object.entries(schema.properties).reduce(
      (
        formData: ObjectConfigFieldData,
        [propertyId, propertySchema]: [string, ConfigFieldSchema],
      ) => {
        const propertyData = data?.[propertyId] ?? schema.default?.[propertyId]
        formData[propertyId] = this.getServiceConfigFieldFormData(propertySchema, propertyData)
        return formData
      },
      Object.assign({}, data),
    )
  }

  private static getArrayServiceConfigFieldFormData(
    schema: ArrayConfigFieldSchema,
    data: ArrayConfigFieldData,
  ): ArrayConfigFieldData {
    return data ?? schema.default?.slice()
  }

  private static getObjectServiceConfigFieldValidityState(
    schema: ObjectConfigFieldSchema,
    data: ObjectConfigFieldData,
  ): ObjectServiceConfigFieldValidityState {
    const errors: ServiceConfigFieldError[] = []
    const properties = Object.entries(schema.properties).reduce(
      (
        props: Dictionary<ServiceConfigFieldValidityState>,
        [propertyId, propertySchema]: [string, ConfigFieldSchema],
      ) => {
        const propertyData = data[propertyId]
        const propertyRequired = schema.required?.includes(propertyId)
        const propertyValidityState = this.getServiceConfigFieldValidityState(
          propertySchema,
          propertyData,
          propertyRequired,
        )
        props[propertyId] = propertyValidityState
        if (
          !!propertyValidityState.errors.length &&
          !errors.some(
            (error: ServiceConfigFieldError) =>
              error.id === ServiceConfigFieldErrorEnum.OBJECT_PROPERTIES,
          )
        ) {
          errors.push({ id: ServiceConfigFieldErrorEnum.OBJECT_PROPERTIES })
        }
        return props
      },
      {},
    )
    return { errors, properties }
  }

  private static getArrayServiceConfigFieldValidityState(
    schema: ArrayConfigFieldSchema,
    data: ArrayConfigFieldData,
  ): ArrayServiceConfigFieldValidityState {
    const errors: ServiceConfigFieldError[] = [] // TODO EP-3835 - complete method
    let items: Dictionary<ServiceConfigFieldValidityState> = {}
    if (schema.items) {
      items = (data as Array<ConfigFieldData>).reduce(
        (
          result: Dictionary<ServiceConfigFieldValidityState>,
          itemData: ConfigFieldData,
          index: number,
          array: Array<ConfigFieldData>,
        ) => {
          const itemValidityState = this.getServiceConfigFieldValidityState(schema.items, itemData)
          result[index] = itemValidityState
          if (
            !!itemValidityState.errors.length &&
            !errors.some(
              (error: ServiceConfigFieldError) =>
                error.id === ServiceConfigFieldErrorEnum.ARRAY_ITEMS,
            )
          ) {
            errors.push({ id: ServiceConfigFieldErrorEnum.ARRAY_ITEMS })
          }
          if (
            array.filter((iteratedItemData: ConfigFieldData) => iteratedItemData === itemData)
              .length > 1
          ) {
            itemValidityState.errors.push({
              id: ServiceConfigFieldErrorEnum.ARRAY_UNIQUE_ITEMS,
            })
          }
          return result
        },
        {},
      )
    }
    if ((!!schema.minItems || schema.minItems === 0) && data.length < schema.minItems) {
      if (schema.minItems === 1) {
        errors.push({
          id: ServiceConfigFieldErrorEnum.ARRAY_MIN_ITEMS_ONE,
        })
      } else {
        errors.push({
          id: ServiceConfigFieldErrorEnum.ARRAY_MIN_ITEMS,
          details: {
            minItems: schema.minItems.toString(),
          },
        })
      }
    }
    if ((!!schema.maxItems || schema.maxItems === 0) && data.length > schema.maxItems) {
      if (schema.maxItems === 1) {
        errors.push({
          id: ServiceConfigFieldErrorEnum.ARRAY_MAX_ITEMS_ONE,
        })
      } else {
        errors.push({
          id: ServiceConfigFieldErrorEnum.ARRAY_MAX_ITEMS,
          details: {
            maxItems: schema.maxItems.toString(),
          },
        })
      }
    }
    return { errors, items }
  }

  private static getStringServiceConfigFieldValidityState(
    schema: StringConfigFieldSchema,
    data: string,
  ): BasicServiceConfigFieldValidityState {
    if (!!schema.enum?.length && !schema.enum.includes(data)) {
      return {
        errors: [
          {
            id: ServiceConfigFieldErrorEnum.STRING_ENUM,
            details: {
              enum: schema.enum.join(', '),
            },
          },
        ],
      }
    }
    const errors: ServiceConfigFieldError[] = []
    if (!!schema.pattern && !data.match(schema.pattern)) {
      errors.push({
        id: ServiceConfigFieldErrorEnum.STRING_PATTERN,
        details: {
          pattern: schema.pattern,
        },
      })
    }
    if ((!!schema.minLength || schema.minLength === 0) && data.length < schema.minLength) {
      errors.push({
        id: ServiceConfigFieldErrorEnum.STRING_MIN_LENGTH,
        details: {
          minLength: schema.minLength.toString(),
        },
      })
    }
    if ((!!schema.maxLength || schema.maxLength === 0) && data.length > schema.maxLength) {
      errors.push({
        id: ServiceConfigFieldErrorEnum.STRING_MAX_LENGTH,
        details: {
          maxLength: schema.maxLength.toString(),
        },
      })
    }
    return { errors }
  }

  private static getNumberServiceConfigFieldValidityState(
    schema: NumberConfigFieldSchema,
    data: number,
  ): BasicServiceConfigFieldValidityState {
    if (typeof data !== 'number') {
      return { errors: [{ id: ServiceConfigFieldErrorEnum.NUMBER_INVALID_TYPE }] }
    }
    if (schema.type === ConfigFieldTypeEnum.INTEGER && !Number.isInteger(data)) {
      return { errors: [{ id: ServiceConfigFieldErrorEnum.NUMBER_INTEGER }] }
    }
    const errors: ServiceConfigFieldError[] = []
    if ((!!schema.minimum || schema.minimum === 0) && data < schema.minimum) {
      errors.push({
        id: ServiceConfigFieldErrorEnum.NUMBER_MINIMUM,
        details: {
          minimum: schema.minimum.toString(),
        },
      })
    }
    if (
      (!!schema.exclusiveMinimum || schema.exclusiveMinimum === 0) &&
      data <= schema.exclusiveMinimum
    ) {
      errors.push({
        id: ServiceConfigFieldErrorEnum.NUMBER_EXCLUSIVE_MINIMUM,
        details: {
          exclusiveMinimum: schema.exclusiveMinimum.toString(),
        },
      })
    }
    if ((!!schema.maximum || schema.maximum === 0) && data > schema.maximum) {
      errors.push({
        id: ServiceConfigFieldErrorEnum.NUMBER_MAXIMUM,
        details: {
          maximum: schema.maximum.toString(),
        },
      })
    }
    if (
      (!!schema.exclusiveMaximum || schema.exclusiveMaximum === 0) &&
      data >= schema.exclusiveMaximum
    ) {
      errors.push({
        id: ServiceConfigFieldErrorEnum.NUMBER_EXCLUSIVE_MAXIMUM,
        details: {
          exclusiveMaximum: schema.exclusiveMaximum.toString(),
        },
      })
    }
    if (
      (!!schema.multipleOf || schema.multipleOf === 0) &&
      !this.isMultipleOf(data, schema.multipleOf)
    ) {
      errors.push({
        id: ServiceConfigFieldErrorEnum.NUMBER_MULTIPLE_OF,
        details: {
          multipleOf: schema.multipleOf.toString(),
        },
      })
    }
    return { errors }
  }

  private static isMultipleOf(data: number, multipleOf: number): boolean {
    const dataValue = Math.round(data / multipleOf) / (1 / multipleOf)
    const decimalDigits = multipleOf.toString().split('.')[1]?.length || 0
    return Number(dataValue.toFixed(decimalDigits)) === data
  }
}
